import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { getIntegrationStatus } from '@/modules/common/api/integration-status';
import {
  DataReconciliationStep,
  dataReconciliationStepToRoute,
  entityTypeToStepMap,
} from '@/modules/common/components/templates/data-reconciliation/components/parts/data-reconciliation-sidebar';
import { SettingsRoutes } from '@/router/routes';

export interface ReconciliationNavigation {
  navigateToPreviousStep: () => void;
  navigateToNextStep: () => void;
  previousStep?: DataReconciliationStep | null;
  nextStep?: DataReconciliationStep | null;
}

export function useReconciliationNavigation(
  currentStep: DataReconciliationStep
): ReconciliationNavigation {
  const navigate = useNavigate();

  const [previousStep, setPreviousStep] = useState<DataReconciliationStep | undefined | null>(
    undefined
  );

  const [nextStep, setNextStep] = useState<DataReconciliationStep | undefined | null>(undefined);

  useEffect(() => {
    let mounted = true;

    async function load() {
      const integrationStatusData = await getIntegrationStatus();

      const currentStepIndex = integrationStatusData.conflicts.findIndex(
        conflict => entityTypeToStepMap.get(conflict.entityType) == currentStep
      );

      const unresolvedConflict = integrationStatusData.conflicts.find(
        conflict =>
          !conflict.isResolved && entityTypeToStepMap.get(conflict.entityType) !== currentStep
      );

      const nextStep =
        (unresolvedConflict?.entityType &&
          entityTypeToStepMap.get(unresolvedConflict?.entityType)) ??
        null;

      if (mounted) {
        setNextStep(nextStep);
      }

      if (currentStepIndex < 0) {
        if (mounted) {
          setPreviousStep(null);
        }
        return;
      }

      if (currentStepIndex == 0) {
        return;
      }

      if (mounted) {
        setPreviousStep(
          entityTypeToStepMap.get(integrationStatusData.conflicts[currentStepIndex - 1].entityType)
        );
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [currentStep]);

  const navigateNext = async () => {
    if (nextStep == null) {
      navigate(SettingsRoutes.Integrations);
    } else if (nextStep) {
      navigate(dataReconciliationStepToRoute[nextStep]);
    }
  };

  const navigateBack = async () => {
    if (previousStep === null) {
      navigate(SettingsRoutes.Integrations);
    } else if (previousStep) {
      navigate(dataReconciliationStepToRoute[previousStep]);
    }
  };

  return {
    navigateToPreviousStep: navigateBack,
    navigateToNextStep: navigateNext,
    previousStep,
    nextStep,
  };
}
